import { createColumnHelper } from '@tanstack/react-table'
import React from 'react'

import { DateCell, PriceCell, RawTextCell, TooltipCell } from '@/components/Table/components/Cell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { COUNTRY_FILTER_OPTIONS, getCountryLabel } from '@/constants/country.constant'
import { SourcingDecisionWorkflowStatus } from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { camelToWords, formatBaseQuotationNumber, formatCurrencyAmount, numberWithCommas } from '@/utils/stringHelper'
import { isNullish } from '@/utils/helper'
import {
  CostBudgetTypeLabel,
  Currency,
  GCPRStatus,
  OfferBookingStatus,
  OfferQuotationDetailInstallAndSupplyUnit,
  OfferQuotationDetailMaintenanceUnit,
  PRStatus,
  TenderDocumentStatus,
} from '@eapproval/offer-shared'
import { MyOfferDisplayLevel } from '@/features/tableProfiles/constants/tableProfile.constant'
import {
  MaintenanceSummaryStatus,
  OfferControlStatus,
  OfferPaymentCreditTerm,
  OfferQuotationStatus,
  OfferStage,
  OfferStatus,
} from '../constants'

const columnHelper = createColumnHelper()

// Set by the server when a cost budget's PRs span more than one currency.
const MIXED_CURRENCY = 'Mixed'

/**
 * Every column the offer lists can show, grouped by the display level it belongs
 * to. My Offer shows all of them; My Sourcing shows the same groups with the
 * sales-side columns left out. Each group takes a `levelMeta` of
 * `{ displayLevel, displayColor }` saying where it sits, so a column is defined
 * once and cannot render differently on the two pages.
 */

export const getCommercialChecklistColumns = (levelMeta) => [
  columnHelper.accessor('commercialCheckListPaymentCreditTerm', {
    header: () => <span>Payment Credit Term</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(OfferPaymentCreditTerm).map((creditTerm) => {
        return {
          value: creditTerm,
          label: creditTerm,
        }
      }),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListPaymentBillingMilestoneMaintenance', {
    header: () => <span>Payment Billing Milestone Maintenance</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListPaymentBillingMilestoneProject', {
    header: () => <span>Payment Billing Milestone Project</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListPaymentRetention', {
    header: () => <span>Payment Retention</span>,

    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'na', label: 'NA' },
        { value: '5%', label: '5%' },
        { value: '10%', label: '10%' },
        { value: '10%Upto5%OfTheContractPrice', label: '10% up to 5% of the contract price' },
        { value: 'other', label: 'other' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListPaymentRetentionRelease', {
    header: () => <span>Payment Retention Release</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'na', label: 'NA' },
        {
          value: 'issuanceOfPracticalCompletionCetificate',
          label: 'Issuance of Practical Completion Certificate',
        },
        {
          value: 'issuanceOfMakingGoodDefectCertificate',
          label: 'Issuance of Making Good Defect Certificate',
        },
        { value: '50OnPCCert50OnDLPCert', label: '50% on PC Cert. + 50% on DLP Cert.' },
        { value: 'other', label: 'other' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListDLPPeriod', {
    header: () => <span>DLP Period</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: '12', label: '12 months' },
        { value: '18', label: '18 months' },
        { value: '24', label: '24 months' },
        { value: 'other', label: 'other' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListDLPCommenceDate', {
    header: () => <span>DLP shall commence from the date of</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'equipmentDelivery', label: 'equipment delivery' },
        { value: 'completionOfWork', label: 'completion of work' },
        {
          value: 'isseanceOfPracticalCompletionCertificate',
          label: 'issuance of Practical Completion Certificate',
        },
        {
          value: 'isseanceOfPracticalCompletionCertificateOfMainContract',
          label: 'issuance of Practical Completion Certificate of Main Contract',
        },
        { value: 'other', label: 'other' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListDLPShallExpire', {
    header: () => <span>DLP Shall Expire</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'yes', label: 'yes' },
        { value: 'no', label: 'no' },
      ],
      ...levelMeta,
    },
  }),

  columnHelper.accessor('commercialCheckListDLPShallExpireCert', {
    header: () => <span>DLP Shall Expire Cert</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'DLPCertForTheMainContract', label: 'DLP Cert. for the Main Contract' },
        { value: 'DLPCertForTheContract', label: 'DLP Cert. for the Contract' },
        { value: 'other', label: 'other' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListIsSiemensStandardTerms', {
    header: () => <span>Is Siemens Standard Terms</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'noWithLegalWaiver', label: 'No (with legal waiver)' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListLDForDelay', {
    header: () => <span>LD For Delay ($ per day)</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListLDForDelayCap', {
    header: () => <span>LD For Delay Cap</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListLDForPerformance', {
    header: () => <span>LD For Performance</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'limited', label: 'LD capped at:' },
        { value: 'unlimited', label: 'Unlimited' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListHasPreviousAgreementBefore', {
    header: () => <span>Has Previous Agreement Before</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),

  columnHelper.accessor('commercialCheckListLDForPerformanceCap', {
    header: () => <span>LD For Performance Cap</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListLiabilityPerEventCap', {
    header: () => <span>Liability Per Event Cap</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListTotalLiabilityCap', {
    header: () => <span>Total Liability Cap</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'limited', label: 'Total Liability capped at ($):' },
        { value: 'limitedPercentage', label: 'Total Liability capped at (%):' },
        { value: 'unlimited', label: 'Unlimited' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListIsExclusiveOfIndirectLoss', {
    header: () => <span>Is Exclusive of Indirect Loss</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListIsExclusiveOfImpliedWarranty', {
    header: () => <span>Is Exclusive of Implied Warranty</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListIsIPRtoUseOnly', {
    header: () => <span>Is IPR To Use Only</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListRequireBond', {
    header: () => <span>Require Bond</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListWarrantyToBeSubmitted', {
    header: () => <span>Warranty To Be Submitted</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListCustomerHasRightToTerminateContract', {
    header: () => <span>Customer Has Right To Terminate Contract</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListCustomerTerminationNotifyPeriod', {
    header: () => <span>Customer Termination Notify Period</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: '30', label: '30 day notice' },
        { value: '60', label: '60 day notice' },
        { value: 'other', label: 'other' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListEmployeeInsuranceProvider', {
    header: () => <span>Employee Insurance Provider</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'employer', label: 'Employer' },
        { value: 'mainContractor', label: 'Main Contractor' },
        { value: 'siemens', label: 'Siemens' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListContractorInsuranceProvider', {
    header: () => <span>Contractor Insurance Provider</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'employer', label: 'Employer' },
        { value: 'mainContractor', label: 'Main Contractor' },
        { value: 'siemens', label: 'Siemens' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListOtherInsuranceProvider', {
    header: () => <span>Other Insurance Provider</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListOpenAccountForEPD', {
    header: () => <span>Open Account for EPD</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListDeclareInCIC', {
    header: () => <span>Declare in CIC</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListAssignmentConsentForSubContract', {
    header: () => <span>Assignment Consent for Sub Contract</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('commercialCheckListNonDisclosureAgreement', {
    header: () => <span>Non Disclosure Agreement</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
]

export const getLegalColumns = (levelMeta) => [
  columnHelper.accessor('legalProjectNature', {
    header: () => <span>Legal Project Name</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('legalProjectCompletionDate', {
    header: () => <span>Legal Project Completion or Contract Expiry Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('legalContactFormPreviouslyReviewed', {
    header: () => <span>Legal Contact Form Previously Reviewed (Specify project, date)</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('legalProjectIPLicense', {
    header: () => <span>Legal Project IP License/Transfer (Reason for transfer)</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('legalLoAProcessLevelRequired', {
    header: () => <span>Legal LoA Process Level Required? (Specify deadline)</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
]

export const getBookingColumns = ({ teamAssistantOptions = [] }, levelMeta) => [
  columnHelper.accessor('bookingCreateDate', {
    header: () => <span>Booking Create Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingStartDate', {
    header: () => <span>Booking Project Start Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingEndDate', {
    header: () => <span>Booking Project End Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingSubmittedDate', {
    header: () => <span>Booking Submitted Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingStartedWorkflow', {
    header: () => <span>Booking Started Workflow</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingTeamAssistant', {
    header: () => <span>Booking Team Assistant</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: teamAssistantOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingWBSNumber', {
    header: () => <span>Booking WBS Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('SONumber', {
    header: () => <span>Booking SO Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingCustomerPONumber', {
    header: () => <span>Booking Customer PO Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingCustomerPODate', {
    header: () => <span>Booking Customer PO Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingBond', {
    header: () => <span>Bond Completed</span>,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingInsurance', {
    header: () => <span>Insurance Completed</span>,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingEPD', {
    header: () => <span>EPD Completed</span>,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingCIC', {
    header: () => <span>CIC Completed</span>,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingBondRemark', {
    header: () => <span>Bond Remark</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingInsuranceRemark', {
    header: () => <span>Insurance Remark</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingEPDRemark', {
    header: () => <span>EPD Remark</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingCICRemark', {
    header: () => <span>CIC Remark</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
]

export const getOfferColumns = (
  {
    salesUserOptions = [],
    engineerUserOptions = [],
    creatorOptions = [],
    teamOptions = [],
    businessUnitOptions = [],
    PMOptions = [],
    costCenterOptions = [],
    GCKOptions = [],
    closeReasons = [],
    SISICOptions = [],
    teamAssistantOptions = [],
    CPMOptions = [],
  },
  levelMeta
) => [
  columnHelper.accessor('status', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values({ ...OfferQuotationStatus, ...OfferBookingStatus, SUPERSEDED: 'Superseded' })
        .concat(closeReasons)
        .map((status) => ({
          label: camelToWords(status),
          value: status,
        })),
      ...levelMeta,
    },
  }),

  columnHelper.accessor('offerCloseDetailedReason', {
    header: () => <span>Close Detailed Reason</span>,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('offerStage', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(OfferStage).map((status) => ({
        label: camelToWords(status),
        value: status,
      })),
      ...levelMeta,
    },
  }),

  ///General Info
  columnHelper.accessor('baseQuotationNumber', {
    header: () => <>Quotation Number</>,
    cell: ({ getValue }) => {
      const baseQuotationNumber = getValue() ?? ''
      const displayQuotationNumber = formatBaseQuotationNumber(baseQuotationNumber)

      return <>{displayQuotationNumber}</>
    },
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('customerNumber', {
    header: () => <>Offer To</>,
    cell: ({ getValue }) => {
      return <>C{getValue()}</>
    },
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('projectName', {
    size: 200,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('businessUnit', {
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: businessUnitOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('team', {
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: teamOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('revision', {
    cell: ({ getValue }) => {
      return <>R{getValue()}</>
    },
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('customer', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('countryOfInstallation', {
    header: () => <span>Country of Installation</span>,
    cell: ({ getValue }) => <TooltipCell text={getCountryLabel(getValue())} />,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: COUNTRY_FILTER_OPTIONS,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('salesResponsible', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: salesUserOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('creator', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: creatorOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('updatedAt', {
    header: () => <span>Last Update</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('createdAt', {
    header: () => <span>Create Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),

  columnHelper.accessor('site', {
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('customerReference', {
    header: () => <span>Customer Ref.</span>,
    meta: {
      ...levelMeta,
    },
  }),

  //Offer Info
  columnHelper.accessor('offerSubmissionDate', {
    header: () => <span>Target Quotation Submission Date</span>,

    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('projectStartDate', {
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('projectEndDate', {
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('projectYear', {
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('projectMonth', {
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('engineers', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: engineerUserOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('manager', {
    header: () => <span>Project Manager</span>,

    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: PMOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('requirePhysicalSignature', {
    header: () => <span>Physical Signature</span>,

    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { label: 'Required', value: 'required' },
        { label: 'Not Required', value: 'notRequired' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('siemensRoleInTheOffer', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { label: 'Main Contractor', value: 'mainContract' },
        { label: 'Install & Supplier', value: 'subContract' },
        { label: 'Other', value: 'other' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('endCustomer', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('SISIC', {
    cell: ({ getValue }) => {
      const code = getValue()
      const selectedOption = SISICOptions.find((opt) => opt.value === code)
      return <TooltipCell text={selectedOption ? selectedOption?.label : code} />
    },
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: SISICOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('LoANumber', {
    header: () => <span>LoA </span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('currency', {
    size: 200,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(Currency).map((currency) => ({
        label: currency,
        value: currency,
      })),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('exchangeRate', {
    size: 200,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('executiveSummary', {
    size: 200,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('quotationCompletedDate', {
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingCompletedDate', {
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('quotationCompletedFY', {
    size: 200,
    header: () => <span>Quotation Completed FY</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingCompletedFY', {
    size: 200,
    header: () => <span>Booking Completed FY</span>,

    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),

  //Sub System
  columnHelper.accessor('systemType', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { label: 'Maintenance', value: 'maintenance' },
        { label: 'Project', value: 'project' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('allSubSystemNewOrRenewal', {
    header: () => <span>New / Renewal</span>,

    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { label: 'New', value: 'new' },
        { label: 'Renewal', value: 'renewal' },
        {
          label: 'Renewal Of Discontinued',
          value: 'renewalOfDiscontinued',
        },
      ],
      ...levelMeta,
    },
  }),

  columnHelper.accessor('profitCenters', {
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: costCenterOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('offerQuotationGrossMargin', {
    cell: ({ getValue }) => {
      return <TooltipCell text={`${numberWithCommas(getValue() || 0)}%`} />
    },
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('offerBookingGrossMargin', {
    cell: ({ getValue }) => {
      return <TooltipCell text={`${numberWithCommas(getValue() || 0)}%`} />
    },
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('offerQuotationContractPrice', {
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('offerBookingContractPrice', {
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),

  columnHelper.accessor('GCKCodes', {
    header: () => <span>GCK</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: GCKOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('equipmentIds', {
    header: () => <span>Equipment IDs</span>,
    cell: RawTextCell,

    meta: {
      ...levelMeta,
    },
  }),

  //Approval

  columnHelper.accessor('quotationSubmittedDate', {
    size: 200,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('quotationCPM', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: CPMOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('bookingCPM', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: CPMOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('nextApprover', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: creatorOptions,
      ...levelMeta,
    },
  }),

  //Damex
  columnHelper.accessor('damexCreateDate', {
    size: 200,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('damexRequireSalesToConfirm', {
    size: 200,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('damexConfirmedBySales', {
    size: 200,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('damexSigned', {
    size: 200,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('damexConfirmDate', {
    size: 200,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('isSOP', {
    header: () => <span>Security of Payment</span>,
    cell: ({ getValue }) => <TooltipCell text={getValue() ? 'Yes' : 'No'} />,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),

  ...getCommercialChecklistColumns(levelMeta),
  ...getLegalColumns(levelMeta),
  ...getBookingColumns({ teamAssistantOptions }, levelMeta),
  columnHelper.accessor('costStructureGCBAU', {
    header: () => <>GC-BAU ($)</>,
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureGCFS', {
    header: () => <>GC-FS ($)</>,
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureGCOther', {
    header: () => <>GC-Other ($)</>,
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureMaterial', {
    header: () => <>Material ($)</>,
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureInternalLabour', {
    header: () => <>Internal Labour ($)</>,
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureSubcontractor', {
    header: () => <>Subcontractor ($)</>,
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureOther', {
    header: () => <>Other ($)</>,
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureOtherExpense', {
    header: () => <>Other Expense ($)</>,
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureMisc', {
    header: () => <>Misc ($)</>,
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureGCBAUPercentage', {
    header: () => <>GC-BAU (%)</>,
    cell: ({ getValue }) => {
      const percentage = getValue() ?? 0

      return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureGCFSPercentage', {
    header: () => <>GC-FS (%)</>,
    cell: ({ getValue }) => {
      const percentage = getValue() ?? 0

      return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureGCOtherPercentage', {
    header: () => <>GC-Other (%)</>,
    cell: ({ getValue }) => {
      const percentage = getValue() ?? 0

      return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureMaterialPercentage', {
    header: () => <>Material (%)</>,
    cell: ({ getValue }) => {
      const percentage = getValue() ?? 0

      return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureInternalLabourPercentage', {
    header: () => <>Internal Labour (%)</>,
    cell: ({ getValue }) => {
      const percentage = getValue() ?? 0

      return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureSubcontractorPercentage', {
    header: () => <>Subcontractor (%)</>,
    cell: ({ getValue }) => {
      const percentage = getValue() ?? 0

      return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureOtherPercentage', {
    header: () => <>Other (%)</>,
    cell: ({ getValue }) => {
      const percentage = getValue() ?? 0

      return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureOtherExpensePercentage', {
    header: () => <>Other Expense (%)</>,
    cell: ({ getValue }) => {
      const percentage = getValue() ?? 0

      return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costStructureMiscPercentage', {
    header: () => <>Misc (%)</>,
    cell: ({ getValue }) => {
      const percentage = getValue() ?? 0

      return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
]

export const getSubSystemColumns = ({ costCenterOptions = [], GCKOptions = [] }, levelMeta) => [
  columnHelper.accessor('subSystemName', {
    cell: RawTextCell,

    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemType', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { label: 'Maintenance', value: 'maintenance' },
        { label: 'Project', value: 'project' },
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemNewOrRenewal', {
    header: () => <span>New / Renewal</span>,

    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { label: 'New', value: 'new' },
        { label: 'Renewal', value: 'renewal' },
        {
          label: 'Renewal Of Discontinued',
          value: 'renewalOfDiscontinued',
        },
      ],
      ...levelMeta,
    },
  }),

  columnHelper.accessor('subSystemProfitCenter', {
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: costCenterOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemQuotationGrossMargin', {
    cell: ({ getValue }) => {
      return <TooltipCell text={`${numberWithCommas(getValue() || 0)}%`} />
    },
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemBookingGrossMargin', {
    cell: ({ getValue }) => {
      return <TooltipCell text={`${numberWithCommas(getValue() || 0)}%`} />
    },
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemQuotationContractPrice', {
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemBookingContractPrice', {
    cell: PriceCell,

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),

  columnHelper.accessor('subSystemGCKCode', {
    header: () => <span>Sub System GCK</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: GCKOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemEquipmentIds', {
    header: () => <span>Sub System Equipment IDs</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemExistingWBSNumber', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemUpcomingQuotationNumber', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemPreviousQuotationNumber', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('subSystemUpcomingQuotationStatus', {
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values({ ...OfferQuotationStatus, ...OfferBookingStatus }).map((status) => {
        return {
          label: camelToWords(status),
          value: status,
        }
      }),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('maintenanceContractStatus', {
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(MaintenanceSummaryStatus).map((status) => {
        return {
          label: camelToWords(status),
          value: status,
        }
      }),
      ...levelMeta,
    },
  }),
]

export const getGCPRColumns = (levelMeta) => [
  columnHelper.accessor('gcprPRNumber', {
    header: () => <span>PR Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprRaiser', {
    header: () => <span>Raiser</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprRequestor', {
    header: () => <span>Requestor</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprRequestDate', {
    header: () => <span>Request Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprTotalAmount', {
    header: () => <span>Total Amount</span>,
    cell: PriceCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprDescriptionOfProduct', {
    header: () => <span>Description of Product / Service</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprExpectedDeliveryDate', {
    header: () => <span>Expected Delivery Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprDeliveryInstructions', {
    header: () => <span>Delivery Instructions</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprStatus', {
    header: () => <span>Status</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(GCPRStatus).map((status) => ({ value: status, label: status })),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprApprovalDueDate', {
    header: () => <span>Approval Due Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprSONumber', {
    header: () => <span>SO# / SAP PR#</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprNextApprover', {
    header: () => <span>Next Approver</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLicenseRequired', {
    header: () => <span>License</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprApprovedDate', {
    header: () => <span>Approved Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprSubmittedDate', {
    header: () => <span>Submitted Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLastApprovalDate', {
    header: () => <span>Last Approval Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprPONumber', {
    header: () => <span>PO #</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLicenseType', {
    header: () => <span>License Type</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLicenseSiteAddress', {
    header: () => <span>Site Address</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLicenseEmailAddress', {
    header: () => <span>Email Address</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLicenseCsid', {
    header: () => <span>CSID</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLicenseExistingDongleNumber', {
    header: () => <span>Existing Dongle Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLicenseFileAttached', {
    header: () => <span>License File Attached</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLicensingManagerRequired', {
    header: () => <span>Licensing Manager Required</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
]

export const getGCPRLineItemColumns = ({ gcprLineItemPurchaseScenarioOptions = [] }, levelMeta) => [
  columnHelper.accessor('gcprLineItemModel', {
    header: () => <span>Model</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemDescription', {
    header: () => <span>Description</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemSAPMaterialNumber', {
    header: () => <span>SAP Material Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemQty', {
    header: () => <span>Qty</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemUnit', {
    header: () => <span>Unit</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemUnitCost', {
    header: () => <span>Unit Cost (HKD)</span>,
    cell: PriceCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemTotalCost', {
    header: () => <span>Total Cost (HKD)</span>,
    cell: PriceCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemPurchaseScenario', {
    header: () => <span>Purchase Scenario</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: gcprLineItemPurchaseScenarioOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemSubSystem', {
    header: () => <span>Sub System</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemVendor', {
    header: () => <span>Vendor</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemPONumber', {
    header: () => <span>PO#</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemCSID', {
    header: () => <span>CSID</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemZUROA', {
    header: () => <span>ZUROA</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemChargeType', {
    header: () => <span>Charge Type</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemBuildingX', {
    header: () => <span>Building X</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemStartDate', {
    header: () => <span>Start Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemSubscriptionId', {
    header: () => <span>Subscription ID</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemTrialEnd', {
    header: () => <span>Trial End</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('gcprLineItemContractEnd', {
    header: () => <span>Contract End</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
]

export const getCostBudgetColumns = ({ sourcingUserOptions = [], vendorOptions = [] }, levelMeta) => [
  columnHelper.accessor('costBudgetRfqNumber', {
    header: () => <span>RFQ #</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetSubSystemName', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetScopeOfSupply', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('sourcingDueDate', {
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetVendorDueDate', {
    header: () => <span>Vendor Due Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetLastSourcingDate', {
    header: () => <span>Last Sourcing Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),

  columnHelper.accessor('procurementResponsible', {
    header: () => <span>Procurement Responsible</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: sourcingUserOptions,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetProcurementCommented', {
    header: () => <span>Procurement Commented</span>,

    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetNumberOfContractorPosted', {
    header: () => <span># of Contractor Posted</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetNumberOfUpload', {
    header: () => <span># of Contractor Uploaded</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetNumberOfPRSubmission', {
    header: () => <span># of PR Submission</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetTotal3rdPartyCost', {
    header: () => <span>Total 3rd Party Cost</span>,
    cell: ({ getValue, row: { original } }) => {
      const currency = original?.costBudgetTotal3rdPartyCostCurrency
      if (currency === MIXED_CURRENCY) {
        return <TooltipCell text="Mixed currencies" tooltip="PRs on this cost budget are in more than one currency" />
      }
      return <TooltipCell text={formatCurrencyAmount(getValue() ?? 0, currency ?? '')} />
    },
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetTotal3rdPartyCostCurrency', {
    header: () => <span>Total 3rd Party Cost Currency</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [...Object.values(Currency), MIXED_CURRENCY].map((currency) => ({
        label: currency,
        value: currency,
      })),
      ...levelMeta,
    },
  }),

  columnHelper.accessor('costBudgetPM040SourcingSelection', {
    header: () => <span>PM040 Sourcing Selection</span>,
    cell: ({ getValue }) => {
      const value = getValue()
      return <TooltipCell text={value ? value.replace(/^(selected|reference)-/, '') : value} />
    },
    meta: {
      filterVariant: FilterType.SELECT,

      filterOptions: [
        { label: 'Selected', value: 'selected' },
        { label: 'Not Selected', value: '(-)' },
        { label: 'Reference Only', value: 'reference' },
        ...vendorOptions,
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetPM040TechnicalSelection', {
    header: () => <span>PM040 Technical Selection</span>,
    cell: ({ getValue }) => {
      const value = getValue()
      return <TooltipCell text={camelToWords(value ? value.replace(/^(selected|reference)-/, '') : value)} />
    },

    meta: {
      filterVariant: FilterType.SELECT,
      filterOptions: [
        { label: 'Selected', value: 'selected' },
        { label: 'Not Selected', value: '(-)' },
        { label: 'Reference Only', value: 'reference' },

        ...vendorOptions,
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetPM040TechnicalSelectionDate', {
    header: () => <span>PM040 Technical Selection Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetPM040TechnicalSelectionAmount', {
    header: () => <span>PM040 Technical Selection Amount</span>,
    cell: ({ getValue, row: { original } }) => {
      const value = getValue()
      const currency = original?.costBudgetPM040TechnicalSelectionCurrency ?? ''
      return <TooltipCell text={isNullish(value) ? '' : formatCurrencyAmount(value, currency)} />
    },
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetPM040TechnicalSelectionCurrency', {
    header: () => <span>PM040 Technical Selection Currency</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(Currency).map((currency) => ({
        label: currency,
        value: currency,
      })),
      ...levelMeta,
    },
  }),

  columnHelper.accessor('costBudgetPM100TechnicalSelection', {
    header: () => <span>PM100 Technical Selection</span>,
    cell: ({ getValue }) => {
      const value = getValue()
      return <TooltipCell text={camelToWords(value ? value.replace(/^(selected|reference)-/, '') : value)} />
    },

    meta: {
      filterVariant: FilterType.SELECT,
      filterOptions: [
        { label: 'Selected', value: 'selected' },
        { label: 'Not Selected', value: '(-)' },
        { label: 'Reference Only', value: 'reference' },

        ...vendorOptions,
      ],
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetQuoteAmount', {
    header: () => <span>Latest Quote Amount</span>,
    cell: ({ getValue, row: { original } }) => {
      const value = getValue()
      const tooltip = original?.lastUploadVendor ? `Last uploaded by ${original?.lastUploadVendor}` : ''
      return (
        <TooltipCell
          text={formatCurrencyAmount(value ?? 0, original?.costBudgetLatestQuoteCurrency ?? '')}
          tooltip={tooltip}
        />
      )
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetNoticedProcurementOfficer', {
    header: () => <span>Noticed Procurement Officer</span>,

    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetRequireProcurementOfficer', {
    header: () => <span>Required Procurement Officer</span>,

    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),

  columnHelper.accessor('costBudgetInternalRemark', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetIsActive', {
    header: () => <span>Cost Budget Is Active</span>,
    cell: ({ getValue }) => <TooltipCell text={getValue() === 'yes' ? 'Yes' : 'No'} />,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetType', {
    header: () => <span>Cost Budget Type</span>,
    cell: ({ getValue }) => <TooltipCell text={CostBudgetTypeLabel[getValue()] || getValue() || ''} />,
    meta: {
      filterVariant: FilterType.SELECT,
      filterOptions: Object.entries(CostBudgetTypeLabel).map(([value, label]) => ({ label, value })),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetLatestQuoteCurrency', {
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(Currency).map((currency) => ({
        label: currency,
        value: currency,
      })),
      ...levelMeta,
    },
  }),

  columnHelper.accessor('costBudgetLargerThanThreshold', {
    header: () => <span>Cost Budget {'>'}100K</span>,

    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetLoARequired', {
    header: () => <span>LoA Required</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetWorkflowStatus', {
    header: () => <span>Workflow Status</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(SourcingDecisionWorkflowStatus).map((status) => ({
        label: status,
        value: status,
      })),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetAddendumStatus', {
    header: () => <span>Addendum Status</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(TenderDocumentStatus).map((status) => ({
        label: status,
        value: status,
      })),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetCTG', {
    header: () => <span>CTG</span>,
    cell: ({ getValue }) => {
      return <TooltipCell text={`${numberWithCommas(getValue() || 0)}`} />
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetCNI', {
    header: () => <span>CNI</span>,
    cell: ({ getValue }) => {
      return <TooltipCell text={`${numberWithCommas(getValue() || 0)}`} />
    },

    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetAppliedSingleSource', {
    header: () => <span>Apply Single Source Procurement ? </span>,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
]

export const getCostBudgetRfqColumns = (levelMeta) => [
  columnHelper.accessor('costBudgetRfqVendorName', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetRfqQuotationUploader', {
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetRfqQuotationAmount', {
    cell: ({ getValue, row: { original } }) => {
      const value = getValue()
      return <TooltipCell text={`$${numberWithCommas(getValue())}`} />
    },
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),

  columnHelper.accessor('costBudgetRfqValidDate', {
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetRfqSubmitDate', {
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),

  // columnHelper.accessor('costBudgetRfqCurrency', {
  //   cell: RawTextCell,
  //   meta: {
  //     filterVariant: FilterType.MULTI_SELECT,
  //     filterOptions: Object.values(Currency).map((currency) => ({
  //       label: currency,
  //       value: currency,
  //     })),
  //     displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
  //     displayColor: 'yellow',
  //   },
  // }),
  columnHelper.accessor('costBudgetRfqPM040SourcingSelected', {
    header: () => <span>PM040 Sourcing</span>,

    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetRfqPM040TechnicalSelected', {
    header: () => <span>PM040 Technical</span>,

    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetRfqPM100TechnicalSelected', {
    header: () => <span>PM100 Technical</span>,

    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('costBudgetRfqSelection', {
    cell: ({ getValue }) => {
      const selection = getValue()
      if (!Array.isArray(selection) || selection.length === 0) {
        return <>(-)</>
      }
      return <TooltipCell text={selection.join(', ')} />
    },

    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        {
          value: 'PM040',
          label: 'PM040',
        },
        {
          value: 'PM040Selected',
          label: 'PM040 Selected',
        },
        {
          value: 'PM0100',
          label: 'PM0100',
        },
        {
          value: 'PM0100Selected',
          label: 'PM0100 Selected',
        },
        {
          value: 'PR',
          label: 'PR',
        },
      ],
      ...levelMeta,
    },
  }),
  // columnHelper.accessor('costBudgetRfqTenderDocumentIndex', {
  //   header: () => <span>Tender Doc Version</span>,
  //   cell: ({ getValue }) => <TooltipCell text={getValue() === 0 ? 'Tender' : 'Addendum ' + getValue()} />,

  //   meta: {
  //     filterVariant: FilterType.SELECT_YES_NO,
  //     displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
  //     displayColor: 'yellow',
  //   },
  // }),
]

export const getPRColumns = (levelMeta) => [
  columnHelper.accessor('prVendor', {
    header: () => <span>Vendor</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prPRNumber', {
    header: () => <span>PR Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prRaiser', {
    header: () => <span>Raiser</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prRequestor', {
    header: () => <span>Requestor</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prRequestDate', {
    header: () => <span>Request Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prTotalAmount', {
    header: () => <span>Total Amount</span>,
    cell: PriceCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prDescriptionOfProduct', {
    header: () => <span>Description of Product / Service</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prExpectedDeliveryDate', {
    header: () => <span>Expected Delivery Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prDeliveryInstructions', {
    header: () => <span>Delivery Instructions</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prStatus', {
    header: () => <span>Status</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(PRStatus).map((status) => ({ value: status, label: status })),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prApprovalDueDate', {
    header: () => <span>Approval Due Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prNextApprover', {
    header: () => <span>Next Approver</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLicenseRequired', {
    header: () => <span>License Required</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prApprovedDate', {
    header: () => <span>Approved Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prSubmittedDate', {
    header: () => <span>Submitted Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLastApprovalDate', {
    header: () => <span>Last Approval Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prPONumber', {
    header: () => <span>PO #</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prSecurityOfPayment', {
    header: () => <span>Security Of Payment</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prSubcontractorAgreementRequired', {
    header: () => <span>Subcontractor Agreement Required</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prSubcontractorAgreementFileAttached', {
    header: () => <span>Subcontractor Agreement Attached</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prInternalDocumentAttached', {
    header: () => <span>Internal Document Attached</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prSAPNumber', {
    header: () => <span>SAP Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prCurrency', {
    header: () => <span>Currency</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(Currency).map((currency) => ({ value: currency, label: currency })),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prRequiredWorkInstructions', {
    header: () => <span>Required Work Instructions</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prWorkInstructionIssued', {
    header: () => <span>Work Instruction Issued</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prWorkInstructionIssuedDate', {
    header: () => <span>Work Instruction Issued Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prWorkInstructionAccepted', {
    header: () => <span>Work Instruction Accepted</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prWorkInstructionAcceptedDate', {
    header: () => <span>Work Instruction Accepted Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLumpSumDiscount', {
    header: () => <span>Lump Sum Discount</span>,
    cell: PriceCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
]

export const getPRLineItemColumns = (levelMeta) => [
  columnHelper.accessor('prLineItemVendor', {
    header: () => <span>Vendor</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemSubSystemName', {
    header: () => <span>Sub System</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemScopeOfWork', {
    header: () => <span>Scope Of Work</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemProductDescription', {
    header: () => <span>Product Description</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemDescription', {
    header: () => <span>Description</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemBrand', {
    header: () => <span>Brand</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemModelNumber', {
    header: () => <span>Model Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemSAPMaterialNumber', {
    header: () => <span>SAP Material Number</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemQuantity', {
    header: () => <span>Quantity</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemUnit', {
    header: () => <span>Unit</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        ...new Set([
          ...Object.values(OfferQuotationDetailInstallAndSupplyUnit),
          ...Object.values(OfferQuotationDetailMaintenanceUnit),
        ]),
      ].map((value) => ({ value, label: value })),
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemUnitRate', {
    header: () => <span>Unit Rate</span>,
    cell: PriceCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemDiscount', {
    header: () => <span>Discount (%)</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemTotalPrice', {
    header: () => <span>Total Price</span>,
    cell: PriceCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemCountry', {
    header: () => <span>Country</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemAL', {
    header: () => <span>AL</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemECCN', {
    header: () => <span>ECCN</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemHSCode', {
    header: () => <span>HS Code</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemStartDate', {
    header: () => <span>Start Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemEndDate', {
    header: () => <span>End Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemSiteLocation', {
    header: () => <span>Site Location</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemNumberOfWorker', {
    header: () => <span>No. Of Worker</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.RANGE,
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemSystem', {
    header: () => <span>System</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
  columnHelper.accessor('prLineItemCategory', {
    header: () => <span>Category</span>,
    cell: RawTextCell,
    meta: {
      ...levelMeta,
    },
  }),
]

/**
 * The accessors in each group, in the order the tables show them. Both pages
 * hide and reorder columns by group, and both list the same ones.
 */

export const COST_BUDGET_COLUMN_ACCESSORS = [
  'costBudgetRfqNumber',
  'costBudgetSubSystemName',
  'costBudgetScopeOfSupply',
  'sourcingDueDate',
  'costBudgetVendorDueDate',
  'costBudgetLastSourcingDate',
  'procurementResponsible',
  'costBudgetProcurementCommented',
  'costBudgetNumberOfContractorPosted',
  'costBudgetNumberOfUpload',
  'costBudgetTotal3rdPartyCost',
  'costBudgetTotal3rdPartyCostCurrency',
  'costBudgetPM040SourcingSelection',
  'costBudgetPM040TechnicalSelection',
  'costBudgetPM040TechnicalSelectionDate',
  'costBudgetPM040TechnicalSelectionAmount',
  'costBudgetPM040TechnicalSelectionCurrency',
  'costBudgetPM100TechnicalSelection',
  'costBudgetQuoteAmount',
  'costBudgetNoticedProcurementOfficer',
  'costBudgetRequireProcurementOfficer',
  'costBudgetInternalRemark',
  'costBudgetLatestQuoteCurrency',
  'costBudgetLargerThanThreshold',
  'costBudgetLoARequired',
  'costBudgetWorkflowStatus',
  'costBudgetAddendumStatus',
  'costBudgetCTG',
  'costBudgetCNI',
  'costBudgetAppliedSingleSource',
]

export const COST_BUDGET_RFQ_COLUMN_ACCESSORS = [
  'costBudgetRfqVendorName',
  'costBudgetRfqQuotationUploader',
  'costBudgetRfqQuotationAmount',
  'costBudgetRfqValidDate',
  'costBudgetRfqSubmitDate',
  'costBudgetRfqCurrency',
  'costBudgetRfqPM040SourcingSelected',
  'costBudgetRfqPM040TechnicalSelected',
  'costBudgetRfqPM100TechnicalSelected',
  'costBudgetRfqSelection',
  'costBudgetRfqTenderDocumentIndex',
]

export const PR_COLUMN_ACCESSORS = [
  'prVendor',
  'prPRNumber',
  'prRaiser',
  'prRequestor',
  'prRequestDate',
  'prTotalAmount',
  'prDescriptionOfProduct',
  'prExpectedDeliveryDate',
  'prDeliveryInstructions',
  'prStatus',
  'prApprovalDueDate',
  'prNextApprover',
  'prLicenseRequired',
  'prApprovedDate',
  'prSubmittedDate',
  'prLastApprovalDate',
  'prPONumber',
  'prSecurityOfPayment',
  'prSubcontractorAgreementRequired',
  'prSubcontractorAgreementFileAttached',
  'prInternalDocumentAttached',
  'prSAPNumber',
  'prCurrency',
  'prRequiredWorkInstructions',
  'prWorkInstructionIssued',
  'prWorkInstructionIssuedDate',
  'prWorkInstructionAccepted',
  'prWorkInstructionAcceptedDate',
  'prLumpSumDiscount',
]

export const PR_LINE_ITEM_COLUMN_ACCESSORS = [
  'prLineItemVendor',
  'prLineItemSubSystemName',
  'prLineItemScopeOfWork',
  'prLineItemProductDescription',
  'prLineItemDescription',
  'prLineItemBrand',
  'prLineItemModelNumber',
  'prLineItemSAPMaterialNumber',
  'prLineItemQuantity',
  'prLineItemUnit',
  'prLineItemUnitRate',
  'prLineItemDiscount',
  'prLineItemTotalPrice',
  'prLineItemCountry',
  'prLineItemAL',
  'prLineItemECCN',
  'prLineItemHSCode',
  'prLineItemStartDate',
  'prLineItemEndDate',
  'prLineItemSiteLocation',
  'prLineItemNumberOfWorker',
  'prLineItemSystem',
  'prLineItemCategory',
]

/**
 * Sub system and GCPR groups only appear on My Offer — Sourcing does not
 * build these levels — so, unlike the groups above, they are not shared.
 */
export const MY_OFFER_SUB_SYSTEM_COLUMN_ACCESSORS = [
  'subSystemName',
  'subSystemType',
  'subSystemNewOrRenewal',
  'subSystemProfitCenter',
  'subSystemQuotationGrossMargin',
  'subSystemBookingGrossMargin',
  'subSystemQuotationContractPrice',
  'subSystemBookingContractPrice',
  'subSystemGCKCode',
  'subSystemEquipmentIds',
  'subSystemExistingWBSNumber',
  'subSystemUpcomingQuotationNumber',
  'subSystemPreviousQuotationNumber',
  'subSystemUpcomingQuotationStatus',
  'maintenanceContractStatus',
]

export const MY_OFFER_GCPR_COLUMN_ACCESSORS = [
  'gcprPRNumber',
  'gcprRaiser',
  'gcprRequestor',
  'gcprRequestDate',
  'gcprTotalAmount',
  'gcprDescriptionOfProduct',
  'gcprExpectedDeliveryDate',
  'gcprDeliveryInstructions',
  'gcprStatus',
  'gcprApprovalDueDate',
  'gcprSONumber',
  'gcprNextApprover',
  'gcprLicenseRequired',
  'gcprApprovedDate',
  'gcprSubmittedDate',
  'gcprLastApprovalDate',
  'gcprPONumber',
  'gcprLicenseType',
  'gcprLicenseSiteAddress',
  'gcprLicenseEmailAddress',
  'gcprLicenseCsid',
  'gcprLicenseExistingDongleNumber',
  'gcprLicenseFileAttached',
  'gcprLicensingManagerRequired',
]

export const MY_OFFER_GCPR_LINE_ITEM_COLUMN_ACCESSORS = [
  'gcprLineItemModel',
  'gcprLineItemDescription',
  'gcprLineItemSAPMaterialNumber',
  'gcprLineItemQty',
  'gcprLineItemUnit',
  'gcprLineItemUnitCost',
  'gcprLineItemTotalCost',
  'gcprLineItemPurchaseScenario',
  'gcprLineItemSubSystem',
  'gcprLineItemVendor',
  'gcprLineItemPONumber',
  'gcprLineItemCSID',
  'gcprLineItemZUROA',
  'gcprLineItemChargeType',
  'gcprLineItemBuildingX',
  'gcprLineItemStartDate',
  'gcprLineItemSubscriptionId',
  'gcprLineItemTrialEnd',
  'gcprLineItemContractEnd',
]

/** One colour per display level, so a column's group is readable at a glance. */
const LEVEL_COLORS = {
  [MyOfferDisplayLevel.OFFER]: 'teal',
  [MyOfferDisplayLevel.SUB_SYSTEM]: 'red',
  [MyOfferDisplayLevel.COST_BUDGET]: 'blue',
  [MyOfferDisplayLevel.COST_BUDGET_RFQ]: 'yellow',
  [MyOfferDisplayLevel.GCPR]: 'purple',
  [MyOfferDisplayLevel.GCPR_LINE_ITEM]: 'purple',
  [MyOfferDisplayLevel.PR]: 'pink',
  [MyOfferDisplayLevel.PR_LINE_ITEM]: 'pink',
}

const at = (displayLevel) => ({ displayLevel, displayColor: LEVEL_COLORS[displayLevel] })

/**
 * The columns of one display level, offer context first. A level that hangs off
 * a cost budget carries the cost budget's columns too, and a PR line item
 * carries its submission's, because a row at that level is read together with
 * the thing it belongs to.
 */
const columnsForLevel = (options, displayLevel) => {
  const offerColumns = getOfferColumns(options, at(MyOfferDisplayLevel.OFFER))
  const costBudgetColumns = () => getCostBudgetColumns(options, at(MyOfferDisplayLevel.COST_BUDGET))
  const prColumns = () => getPRColumns(at(MyOfferDisplayLevel.PR))

  switch (displayLevel) {
    case MyOfferDisplayLevel.SUB_SYSTEM:
      return [...offerColumns, ...getSubSystemColumns(options, at(MyOfferDisplayLevel.SUB_SYSTEM))]
    case MyOfferDisplayLevel.COST_BUDGET:
      return [...offerColumns, ...costBudgetColumns()]
    case MyOfferDisplayLevel.COST_BUDGET_RFQ:
      return [
        ...offerColumns,
        ...costBudgetColumns(),
        ...getCostBudgetRfqColumns(at(MyOfferDisplayLevel.COST_BUDGET_RFQ)),
      ]
    case MyOfferDisplayLevel.GCPR:
      return [...offerColumns, ...getGCPRColumns(at(MyOfferDisplayLevel.GCPR))]
    case MyOfferDisplayLevel.GCPR_LINE_ITEM:
      return [...offerColumns, ...getGCPRLineItemColumns(options, at(MyOfferDisplayLevel.GCPR_LINE_ITEM))]
    case MyOfferDisplayLevel.PR:
      return [...offerColumns, ...costBudgetColumns(), ...prColumns()]
    case MyOfferDisplayLevel.PR_LINE_ITEM:
      return [
        ...offerColumns,
        ...costBudgetColumns(),
        ...prColumns(),
        ...getPRLineItemColumns(at(MyOfferDisplayLevel.PR_LINE_ITEM)),
      ]
    default:
      return offerColumns
  }
}

/**
 * `hiddenColumns` is the whole of the difference between the two offer lists: a
 * column added here reaches both pages unless it is named as hidden.
 */
export const buildOfferListColumns = (options, displayLevel, hiddenColumns) =>
  hiddenColumns
    ? columnsForLevel(options, displayLevel).filter((column) => !hiddenColumns.has(column.accessorKey))
    : columnsForLevel(options, displayLevel)

export const getMyOfferTableColumns = (options, displayLevel = MyOfferDisplayLevel.OFFER) =>
  buildOfferListColumns(options, displayLevel)

/** Header overrides for columns whose accessor name reads poorly as a title; shared by My Offer and My Sourcing. */
export const myOfferTableHeaders = {
  offerCloseDetailedReason: 'Close Detailed Reason',
  customerNumber: 'Offer To',
  updatedAt: 'Last Update',
  createdAt: 'Create Date',
  customerReference: 'Customer Ref.',
  requirePhysicalSignature: 'Physical Signature',
  LoANumber: 'LoA',
  GCKCode: 'GCK',
  bookingBond: 'Bond Completed',
  bookingInsurance: 'Insurance Completed',
  bookingEPD: 'EPD Completed',
  bookingCIC: 'CIC Completed',
  bookingBondRemark: 'Bond Remark',
  bookingInsuranceRemark: 'Insurance Remark',
  bookingEPDRemark: 'EPD Remark',
  bookingCICRemark: 'CIC Remark',
}

/**
 * The columns My Sourcing does not show: pricing, margins, commercial terms,
 * legal, Damex and cost structure are the sales side of an offer. The sub-
 * contract assignment consent stays, alone out of the commercial checklist,
 * because it is a sourcing term. Mirrors SOURCING_HIDDEN_FILTERS on the server,
 * which also drops legalConclusion — a filter with no column of its own.
 */
export const SOURCING_HIDDEN_COLUMNS = new Set([
  // Offer
  'currency',
  'exchangeRate',
  'executiveSummary',
  'offerQuotationGrossMargin',
  'offerBookingGrossMargin',
  'offerQuotationContractPrice',
  'offerBookingContractPrice',
  'GCKCodes',
  'equipmentIds',

  // Damex
  'damexCreateDate',
  'damexRequireSalesToConfirm',
  'damexConfirmedBySales',
  'damexSigned',
  'damexConfirmDate',

  // Commercial checklist, except the sub-contract assignment consent
  'commercialCheckListPaymentCreditTerm',
  'commercialCheckListPaymentBillingMilestoneMaintenance',
  'commercialCheckListPaymentBillingMilestoneProject',
  'commercialCheckListPaymentRetention',
  'commercialCheckListPaymentRetentionRelease',
  'commercialCheckListDLPPeriod',
  'commercialCheckListDLPCommenceDate',
  'commercialCheckListDLPShallExpire',
  'commercialCheckListDLPShallExpireCert',
  'commercialCheckListIsSiemensStandardTerms',
  'commercialCheckListLDForDelay',
  'commercialCheckListLDForDelayCap',
  'commercialCheckListLDForPerformance',
  'commercialCheckListLDForPerformanceCap',
  'commercialCheckListHasPreviousAgreementBefore',
  'commercialCheckListLiabilityPerEventCap',
  'commercialCheckListTotalLiabilityCap',
  'commercialCheckListIsExclusiveOfIndirectLoss',
  'commercialCheckListIsExclusiveOfImpliedWarranty',
  'commercialCheckListIsIPRtoUseOnly',
  'commercialCheckListRequireBond',
  'commercialCheckListWarrantyToBeSubmitted',
  'commercialCheckListCustomerHasRightToTerminateContract',
  'commercialCheckListCustomerTerminationNotifyPeriod',
  'commercialCheckListEmployeeInsuranceProvider',
  'commercialCheckListContractorInsuranceProvider',
  'commercialCheckListOtherInsuranceProvider',
  'commercialCheckListOpenAccountForEPD',
  'commercialCheckListDeclareInCIC',
  'commercialCheckListNonDisclosureAgreement',

  // Legal
  'legalProjectNature',
  'legalProjectCompletionDate',
  'legalContactFormPreviouslyReviewed',
  'legalProjectIPLicense',
  'legalLoAProcessLevelRequired',

  // Booking sign-offs
  'bookingBond',
  'bookingInsurance',
  'bookingEPD',
  'bookingCIC',
  'bookingBondRemark',
  'bookingInsuranceRemark',
  'bookingEPDRemark',
  'bookingCICRemark',

  // Cost structure
  'costStructureGCBAU',
  'costStructureGCFS',
  'costStructureGCOther',
  'costStructureMaterial',
  'costStructureInternalLabour',
  'costStructureSubcontractor',
  'costStructureOther',
  'costStructureOtherExpense',
  'costStructureMisc',
  'costStructureGCBAUPercentage',
  'costStructureGCFSPercentage',
  'costStructureGCOtherPercentage',
  'costStructureMaterialPercentage',
  'costStructureInternalLabourPercentage',
  'costStructureSubcontractorPercentage',
  'costStructureOtherPercentage',
  'costStructureOtherExpensePercentage',
  'costStructureMiscPercentage',

  // Sub system pricing
  'subSystemQuotationGrossMargin',
  'subSystemBookingGrossMargin',
  'subSystemQuotationContractPrice',
  'subSystemBookingContractPrice',
  'subSystemGCKCode',
  'subSystemEquipmentIds',
])

/** Every accessor the list can show at any level, for the given hidden set. */
export const allOfferListAccessors = (options, hiddenColumns) => {
  const accessors = new Set()

  for (const displayLevel of Object.values(MyOfferDisplayLevel)) {
    for (const column of buildOfferListColumns(options, displayLevel, hiddenColumns)) {
      accessors.add(column.accessorKey)
    }
  }

  return accessors
}

/**
 * Columns of the levels the table is not showing, switched off. They are absent
 * from the rendered table either way; this keeps them out of the query and out
 * of the Excel export, both of which read columnVisibility.
 */
export const hideColumnsOutsideLevel = (allAccessors, levelColumns) => {
  const shown = new Set(levelColumns.map((column) => column.accessorKey))
  const hidden = {}

  for (const accessor of allAccessors) {
    if (!shown.has(accessor)) {
      hidden[accessor] = false
    }
  }

  return hidden
}

/**
 * A saved profile lists the columns that existed when it was saved. Columns
 * added since are switched off, so a profile keeps showing what its owner chose
 * and new columns are something they turn on rather than find appended to the
 * right of their table. A profile that has never been customised orders nothing
 * and so shows everything.
 */
export const hideColumnsAddedSinceProfileSaved = (tableDetails, allAccessors) => {
  const savedOrder = tableDetails?.columnOrder

  if (!savedOrder?.length) {
    return tableDetails
  }

  const known = new Set(savedOrder)
  const columnVisibility = { ...tableDetails.columnVisibility }

  for (const accessor of allAccessors) {
    if (!known.has(accessor) && columnVisibility[accessor] === undefined) {
      columnVisibility[accessor] = false
    }
  }

  return { ...tableDetails, columnVisibility }
}
